import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import api from '../../services/api';
import toast from 'react-hot-toast';
import DashboardHeader from '../../Components/common/DashboardHeader';
import {
  Users,
  ClipboardList,
  Activity,
  PhoneCall,
  Calendar,
  Clock,
  TrendingUp,
  UserCheck,
  ArrowRight,
  ChevronRight,
  ShieldAlert,
  Inbox,
  Award
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const CCHeadDashboard = () => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/cc-head');
      if (response.data?.success) {
        setData(response.data.data);
      } else {
        toast.error('Failed to parse dashboard data');
      }
    } catch (error) {
      console.error('Error fetching CC Head dashboard:', error);
      toast.error('Failed to load supervisor dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Socket auto-refresh when events trigger
  useEffect(() => {
    if (!socket) return;

    const handleRealTimeUpdate = (update) => {

      fetchDashboardData();
    };

    socket.on('personal_notification', handleRealTimeUpdate);
    socket.on('system_activity', handleRealTimeUpdate);
    socket.on('lead_updated', handleRealTimeUpdate);

    return () => {
      socket.off('personal_notification', handleRealTimeUpdate);
      socket.off('system_activity', handleRealTimeUpdate);
      socket.off('lead_updated', handleRealTimeUpdate);
    };
  }, [socket]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-[#143E78]/10 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-[#143E78] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  const {
    stats = {},
    agentStats = [],
    recentActivity = [],
    followUpCounts = { pending: 0, overdue: 0 },
    pendingFollowUps = [],
    overdueFollowUps = [],
    pendingRequestFollowUps = [],
    staleLeads = [],
    onlineUsers = []
  } = data;

  // Lead Funnel Chart Setup
  const funnelChartData = [
    { name: 'New', value: stats.new || 0, fill: '#3b82f6' },
    { name: 'Screening', value: stats.screening || 0, fill: '#60a5fa' },
    { name: 'Patient Ready', value: stats.patient_ready || 0, fill: '#10b981' },
    { name: 'Doc Verify', value: stats.doc_verification || 0, fill: '#059669' },
    { name: 'Clinical Appr', value: stats.clinical_approval || 0, fill: '#0d9488' },
    { name: 'Surgery Ready', value: stats.surgery_ready || 0, fill: '#8b5cf6' },
    { name: 'Scheduling', value: stats.scheduling || 0, fill: '#a78bfa' },
    { name: 'Pay Pending', value: stats.payment_pending || 0, fill: '#f59e0b' },
    { name: 'Paid / Surgery', value: (stats.paid || 0) + (stats.surgery || 0), fill: '#16a34a' },
    { name: 'Completed', value: stats.completed || 0, fill: '#15803d' }
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">

      {/* 1. Header component */}
      <DashboardHeader user={user} />

      {/* ⚠️ STALE LEADS WARNING BANNER
      {staleLeads.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-[2rem] p-6 shadow-sm animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600 shrink-0">
                <ShieldAlert className="w-6 h-6 animate-bounce" />
              </div>
              <div>
                <h3 className="text-sm font-black text-amber-950 uppercase tracking-wider">URGENT: LEADS WAITING TOO LONG (OVER 10 MINS)</h3>
                <p className="text-xs text-amber-800/80 font-bold mt-0.5">The following new leads have been waiting for more than 10 minutes. Please take immediate action to assign or process them.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
              {staleLeads.map(lead => {
                const diffMins = Math.round((new Date() - new Date(lead.createdAt)) / 60000);
                const timeStr = diffMins < 60
                  ? `${diffMins} mins`
                  : `${Math.floor(diffMins / 60)} hr ${diffMins % 60} mins`;
                const ccName = lead.assignedCustomerCare
                  ? `${lead.assignedCustomerCare.firstName} ${lead.assignedCustomerCare.lastName}`
                  : 'Unassigned';

                return (
                  <div key={lead._id} className="p-4 bg-white rounded-2xl border border-amber-100 flex justify-between items-center hover:shadow-md transition-all">
                    <div className="min-w-0 flex-1 pr-2">
                      <p className="text-xs font-black text-slate-900 truncate">{lead.patientName}</p>
                      <p className="text-[10px] text-slate-400 font-semibold truncate mt-0.5">{lead.patientPhone}</p>
                      <p className="text-[10px] text-slate-500 font-bold mt-1 truncate">
                        CC: <span className="text-slate-700 font-black">{ccName}</span>
                      </p>
                      <p className="text-[9px] text-amber-600 font-black mt-1 uppercase tracking-wider">
                        Pending: {timeStr}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/leads/${lead._id}`)}
                      className="p-2 bg-amber-600 text-white rounded-xl shadow hover:bg-amber-700 transition-colors shrink-0"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      */}

      {/* 2. Top-level Performance / Alert Ribbon */}
      {(followUpCounts.overdue > 0) && (
        <div className="bg-rose-50 border border-rose-200 rounded-[2rem] p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-rose-100 rounded-2xl flex items-center justify-center text-rose-600 shrink-0">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-sm font-black text-rose-950 uppercase tracking-wider">Urgent Action Required</h3>
              <p className="text-xs text-rose-800/80 font-bold mt-0.5">There are currently {followUpCounts.overdue} overdue patient follow-up calls pending action from your agents.</p>
            </div>
          </div>
          <Link
            to="/follow-ups"
            className="px-5 py-3 bg-rose-600 hover:bg-rose-700 text-white font-black rounded-2xl text-[10px] sm:text-xs uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg"
          >
            Manage Follow-ups <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* 3. Core Analytical Widgets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          label="Total Leads"
          value={stats.total || 0}
          subLabel="Total Active Funnel Size"
          icon={ClipboardList}
          color="blue"
          trend="Overview"
        />
        <MetricCard
          label="Screening & Prep"
          value={(stats.new || 0) + (stats.screening || 0)}
          subLabel="Awaiting Review/Verification"
          icon={Activity}
          color="indigo"
          trend="Active Intake"
        />
        <MetricCard
          label="Overdue Calls"
          value={followUpCounts.overdue || 0}
          subLabel="Requires immediate response"
          icon={PhoneCall}
          color="rose"
          trend={followUpCounts.overdue > 0 ? "Alert" : "Clean"}
        />
        <MetricCard
          label="Closed Success"
          value={stats.completed || 0}
          subLabel="Successfully converted cases"
          icon={Award}
          color="emerald"
          trend="Success"
        />
      </div>

      {/* 4. Main Analytics Chart & Funnel Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">

        {/* Case Distribution Funnel */}
        <div className="lg:col-span-8 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 shadow-2xl shadow-slate-200/40 border border-slate-100 relative overflow-hidden flex flex-col">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full -mr-32 -mt-32 blur-3xl opacity-50"></div>
          <div className="flex justify-between items-center mb-6 relative z-10">
            <div>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">Lead Funnel Distribution</h3>
              <p className="text-gray-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-1">Lead counts across stages</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-xl border border-slate-100">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest">Active Funnel</span>
            </div>
          </div>

          <div className="flex-1 min-h-[300px] mt-4 relative z-10">
            {funnelChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320} minWidth={1} minHeight={1}>
                <BarChart data={funnelChartData}>
                  <CartesianGrid strokeDasharray="6 6" vertical={false} stroke="#E2E8F0" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748B', fontSize: 9, fontWeight: 900 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748B', fontSize: 10, fontWeight: 900 }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(20, 62, 120, 0.03)', radius: 10 }}
                    content={<CustomChartTooltip />}
                  />
                  <Bar dataKey="value" radius={[8, 8, 8, 8]} barSize={38}>
                    {funnelChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex flex-col items-center justify-center bg-slate-50/50 rounded-3xl border-2 border-dashed border-slate-100 p-8 text-center">
                <Inbox className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No Active Leads to Plot</p>
              </div>
            )}
          </div>
        </div>

        {/* CC Agent Productivity / Workloads */}
        <div className="lg:col-span-4 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 shadow-2xl shadow-slate-200/40 border border-slate-100 flex flex-col">
          <div className="mb-6">
            <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight font-sans">Agent Workloads</h3>
            <p className="text-gray-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-1">Leads assigned per CC Agent</p>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto max-h-[340px] pr-1">
            {agentStats.length > 0 ? (
              agentStats.map((agent) => (
                <div
                  key={agent._id}
                  className="p-4 bg-slate-50 border border-slate-100 rounded-2xl hover:bg-slate-100/80 hover:shadow-sm transition-all"
                >
                  <div className="flex justify-between items-start">
                    <div className="min-w-0">
                      <p className="text-sm font-black text-slate-900 truncate">
                        {agent.firstName} {agent.lastName}
                      </p>
                      <p className="text-[10px] text-slate-400 font-semibold truncate mt-0.5">
                        {agent.email}
                      </p>
                      <p className="text-[10px] text-slate-400 font-semibold truncate">
                        {agent.phone || 'N/A'}
                      </p>
                    </div>
                    <div className="text-right shrink-0 flex gap-4">
                      <div>
                        <span className="text-base font-black text-amber-600 block leading-none">
                          {agent.pendingLeadsCount}
                        </span>
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider block mt-1">
                          Pending
                        </span>
                      </div>
                      <div>
                        <span className="text-base font-black text-emerald-600 block leading-none">
                          {agent.processedLeadsCount}
                        </span>
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider block mt-1">
                          Processed
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="h-48 flex flex-col items-center justify-center text-center">
                <Users className="w-8 h-8 text-slate-200 mb-2" />
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">No Active Agents Found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 5. Live Activity Logs & Overdue Followups List */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">

        {/* Live CC Agent Activity Stream */}
        <div className="lg:col-span-8 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 shadow-2xl shadow-slate-200/40 border border-slate-100">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">Live CC activity</h3>
              <p className="text-gray-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-1">Real-time actions by CC agents</p>
            </div>
          </div>

          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
            {recentActivity.length > 0 ? (
              recentActivity.map((log) => (
                <div
                  key={log._id}
                  className="flex items-start justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-slate-100/50 hover:shadow-sm transition-all gap-4"
                >
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-10 h-10 bg-white rounded-xl shadow-sm border border-slate-100 flex items-center justify-center text-blue-600 shrink-0">
                      <Activity className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-black text-slate-900 uppercase tracking-tight">
                        {log.action?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed mt-0.5">
                        {log.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[8px] font-black text-slate-400 uppercase">
                          {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'System'}
                        </span>
                        <div className="w-1.5 h-1.5 bg-slate-200 rounded-full"></div>
                        <span className="text-[8px] font-black text-slate-400 uppercase">
                          {new Date(log.createdAt).toLocaleDateString()} at {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-12 text-center">
                <Clock className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-xs font-bold text-slate-400">No activity logged in this feed yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Priority Followup Queue */}
        <div className="lg:col-span-4 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 shadow-2xl shadow-slate-200/40 border border-slate-100 flex flex-col">
          <div className="mb-6">
            <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">Follow-Up Queue</h3>
            <p className="text-gray-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-1">Pending and Overdue calls</p>
          </div>

          <div className="flex-1 space-y-3 max-h-[500px] overflow-y-auto pr-1">

            {/* Overdue Items first */}
            {overdueFollowUps.length > 0 && (
              <div className="space-y-2">
                <p className="text-[8px] font-black text-rose-500 uppercase tracking-widest px-1">Overdue Calls</p>
                {overdueFollowUps.map((item) => (
                  <div key={item._id} className="p-3.5 bg-rose-50 border border-rose-100 rounded-2xl flex justify-between items-center">
                    <div className="min-w-0">
                      <p className="text-xs font-black text-rose-950 truncate">{item.patientName}</p>
                      <p className="text-[9px] text-rose-700/80 font-bold mt-0.5 flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-rose-600" /> {new Date(item.scheduledDate || item.nextFollowUpDate).toLocaleDateString()}
                      </p>
                    </div>
                    <Link
                      to={`/follow-ups/${item._id}`}
                      className="p-2 bg-rose-600 text-white rounded-xl shadow hover:bg-rose-700 shrink-0"
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                ))}
              </div>
            )}

            {/* Pending Items */}
            <div className="space-y-2 pt-2">
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest px-1">Upcoming Calls</p>
              {pendingFollowUps.length > 0 ? (
                pendingFollowUps.map((item) => (
                  <div key={item._id} className="p-3.5 bg-slate-50 border border-slate-100 rounded-2xl flex justify-between items-center">
                    <div className="min-w-0">
                      <p className="text-xs font-black text-slate-900 truncate">{item.patientName}</p>
                      <p className="text-[9px] text-slate-500 font-bold mt-0.5 flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-slate-400" /> {new Date(item.scheduledDate || item.nextFollowUpDate).toLocaleDateString()}
                      </p>
                    </div>
                    <Link
                      to={`/follow-ups/${item._id}`}
                      className="p-2 bg-slate-800 text-white rounded-xl shadow hover:bg-slate-900 shrink-0"
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                ))
              ) : (
                overdueFollowUps.length === 0 && (
                  <div className="py-10 text-center">
                    <UserCheck className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">All caught up!</p>
                  </div>
                )
              )}
            </div>

          </div>
        </div>

      </div>


      {/* 👥 REAL-TIME ONLINE TEAM */}
      <div className="grid grid-cols-1 gap-6 sm:gap-8 mt-8">
        <div className="bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 shadow-2xl shadow-slate-200/40 border border-slate-100 flex flex-col">
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">Active Online Team</h3>
              <p className="text-gray-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-1">Real-time sessions and connection durations</p>
            </div>
            <span className="flex h-3 w-3 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-h-[380px] overflow-y-auto pr-1">
            {onlineUsers.length > 0 ? (
              onlineUsers.map(onlineUser => {
                const connectedSince = new Date(onlineUser.connectedAt);
                const diffMs = new Date() - connectedSince;
                const diffMins = Math.floor(diffMs / 60000);
                const diffSecs = Math.floor((diffMs % 60000) / 1000);
                const durationText = diffMins < 1
                  ? `${diffSecs} sec${diffSecs !== 1 ? 's' : ''}`
                  : diffMins < 60
                    ? `${diffMins} min${diffMins !== 1 ? 's' : ''}`
                    : `${Math.floor(diffMins / 60)} hr ${diffMins % 60} mins`;

                return (
                  <div key={onlineUser.userId} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex justify-between items-center hover:bg-slate-100/50 hover:shadow-sm transition-all">
                    <div className="min-w-0">
                      <p className="text-xs font-black text-slate-900 truncate">
                        {onlineUser.firstName} {onlineUser.lastName}
                      </p>
                      <p className="text-[9px] text-slate-400 font-black uppercase tracking-wider mt-0.5">
                        {onlineUser.role.replace(/_/g, ' ')}
                      </p>
                      <p className="text-[9px] text-slate-500 font-semibold truncate mt-1">
                        {onlineUser.email}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-[10px] font-black text-emerald-600 block">
                        Online
                      </span>
                      <span className="text-[8px] font-bold text-slate-400 block mt-0.5">
                        For {durationText}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full py-12 text-center">
                <Users className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-xs font-bold text-slate-400">No active team members online</p>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

// Sub-Component: Metric Card
const MetricCard = ({ label, value, subLabel, icon: Icon, color, trend }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100'
  };

  return (
    <div className="bg-white p-5 sm:p-6 rounded-[24px] border border-slate-100 shadow-xl shadow-slate-200/30 group hover:shadow-2xl hover:-translate-y-1 transition-all">
      <div className="flex justify-between items-start mb-4">
        <div className={`w-10 h-10 ${colorMap[color].split(' ')[0]} rounded-xl flex items-center justify-center ${colorMap[color].split(' ')[1]} group-hover:rotate-12 transition-transform`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border ${colorMap[color].split(' ')[2]}`}>
            {trend}
          </span>
        )}
      </div>
      <h4 className="text-slate-400 text-[9px] sm:text-[10px] font-black uppercase tracking-widest mb-1">{label}</h4>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tighter leading-none">{value}</span>
      </div>
      <p className="text-[8px] sm:text-[9px] text-slate-400 font-bold mt-2.5 tracking-tight">{subLabel}</p>
    </div>
  );
};

// Recharts Custom Tooltip
const CustomChartTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 text-white p-4 rounded-[16px] shadow-2xl border border-white/10 animate-in zoom-in duration-200">
        <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">{payload[0].payload.name}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-black">{payload[0].value}</span>
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Leads</span>
        </div>
      </div>
    );
  }
  return null;
};

export default CCHeadDashboard;